import type { CommunityCoverImage } from "@upsquad/shared";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/src/common/components/ui";

interface CommunityCoverCarouselProps {
	images: CommunityCoverImage[];
}

export function CommunityCoverCarousel({ images }: CommunityCoverCarouselProps) {
	const { t } = useTranslation("community");
	const [index, setIndex] = useState(0);

	const sorted = useMemo(() => [...images].sort((a, b) => a.order - b.order), [images]);

	useEffect(() => {
		setIndex(0);
	}, [images]);

	if (sorted.length === 0) return null;

	const current = sorted[index] ?? sorted[0];

	const prev = () => setIndex((i) => (i - 1 + sorted.length) % sorted.length);
	const next = () => setIndex((i) => (i + 1) % sorted.length);

	return (
		<div className="relative w-full overflow-hidden rounded-2xl aspect-video bg-content2">
			{current && <img src={current.url} alt={current.alt ?? ""} className="w-full h-full object-cover" loading="lazy" />}
			{sorted.length > 1 && (
				<>
					<Button
						isIconOnly
						variant="ghost"
						size="sm"
						onPress={prev}
						aria-label={t("carousel_prev")}
						className="absolute left-3 top-1/2 -translate-y-1/2 bg-background/60 backdrop-blur-sm rounded-full p-2"
					>
						<ChevronLeft className="w-4 h-4" />
					</Button>
					<Button
						isIconOnly
						variant="ghost"
						size="sm"
						onPress={next}
						aria-label={t("carousel_next")}
						className="absolute right-3 top-1/2 -translate-y-1/2 bg-background/60 backdrop-blur-sm rounded-full p-2"
					>
						<ChevronRight className="w-4 h-4" />
					</Button>
					<div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
						{sorted.map((_, i) => (
							<button
								key={i}
								type="button"
								onClick={() => setIndex(i)}
								className={`w-2 h-2 rounded-full transition-colors ${i === index ? "bg-foreground" : "bg-foreground/30"}`}
								aria-label={`Go to image ${i + 1}`}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
}
